import React, {useState} from "react";
import {useSnackbar} from "notistack";
import {Box, Stack, Grid, Typography, Chip} from "@mui/material";
import apiClient from "../../api/apiClient";
import {usePreloaderStore} from "../../stores";
import {TextInput} from "../../../components/ui/inputs";
import {PrimaryButton, DeleteButton} from "../../../components/ui/buttons";

export default function DozorCodeCreator({codes, currentPoint, isCorrect, onChangeFunction}) {
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();

    const title = isCorrect ? "Правильные коды" : "Неправильные коды";

    const handleDeleteOneCode = async (code) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/dozor/admin/codes/delete/${code.id}`);
            enqueueSnackbar("Код удалён", {variant: "success"});
            onChangeFunction("DELETE_ONE", code);
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении кода", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    const handleDeleteAllCodes = async () => {
        showPreloader();
        try {
            await apiClient.delete(
                `/api/dozor/admin/codes/delete-bulk?isCorrect=${isCorrect}&pointId=${currentPoint.id}`
            );
            enqueueSnackbar("Коды удалены", {variant: "success"});
            onChangeFunction("DELETE", codes);
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении кодов", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    return (
        <Box
            sx={{
                height: '100%',
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
            }}
        >
            <Stack spacing={2} sx={{ height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {title}
                </Typography>

                {/* Список кодов */}
                <Box
                    sx={{
                        minHeight: 80,
                        maxHeight: 120,
                        overflowY: 'auto',
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {codes.map((code, index) => (
                            <Chip
                                key={index}
                                label={code.value || code.codeValue}
                                size="small"
                                color={isCorrect ? "success" : "error"}
                                variant="outlined"
                                onDelete={() => handleDeleteOneCode(code)}
                            />
                        ))}
                        {codes.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Нет кодов
                            </Typography>
                        )}
                    </Box>
                </Box>

                <DeleteButton
                    onClick={handleDeleteAllCodes}
                    disabled={codes.length === 0}
                    size="small"
                >
                    Очистить
                </DeleteButton>

                {/* Генератор кодов */}
                <CodeGenerator
                    isCorrect={isCorrect}
                    currentPoint={currentPoint}
                    onChangeFunction={onChangeFunction}
                />
            </Stack>
        </Box>
    );
}

function CodeGenerator({isCorrect, currentPoint, onChangeFunction}) {
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();

    const [mask, setMask] = useState("");
    const [cost, setCost] = useState("");
    const [count, setCount] = useState("");

    const handleGenerate = async () => {
        if (!mask || !cost || !count) {
            enqueueSnackbar("Заполните все поля", {variant: "warning"});
            return;
        }

        showPreloader();
        try {
            const response = await apiClient.post('/api/dozor/admin/codes/generate', {
                pointId: currentPoint.id,
                mask: mask,
                cost: parseInt(cost),
                count: parseInt(count),
                isCorrect: isCorrect
            });
            enqueueSnackbar("Коды сгенерированы", {variant: "success"});
            onChangeFunction("APPEND", response.data);
            setMask("");
            setCost("");
            setCount("");
        } catch (error) {
            enqueueSnackbar("Ошибка при генерации кодов", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                <strong>Маска</strong> — [**DR**], где * — любые числа 1-9<br/>
                <strong>Цена</strong> — положительные и отрицательные значения<br/>
                <strong>Кол-во</strong> — неотрицательные целые числа
            </Typography>

            <Stack spacing={1.5}>
                <TextInput
                    label="Маска"
                    value={mask}
                    onChange={e => setMask(e.target.value)}
                    size="small"
                />
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextInput
                                label="Цена"
                                value={cost}
                                onChange={e => setCost(e.target.value)}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextInput
                                label="Кол-во"
                                value={count}
                                onChange={e => setCount(e.target.value)}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Box>
                <PrimaryButton onClick={handleGenerate} size="small">
                    Сгенерировать
                </PrimaryButton>
            </Stack>
        </Box>
    );
}
